import { Button } from '@/components/ui/button';
import type { MediaAsset } from '@/hooks/use-cases';
import { api } from '@/lib/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Star, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

export type CaseMediaSelection = { mediaAssetId: string; role: 'hero' | 'gallery'; sortOrder: number; cropX: number };
type SelectableRole = 'hero' | 'gallery';

function Preview({ asset, caption }: { asset: MediaAsset; caption?: string }) {
  return (
    <figure className="space-y-1">
      <img src={`/api/media/${asset.id}/file?width=1000`} alt="" className="block w-full rounded-md border" />
      {caption && <figcaption className="text-xs text-muted-foreground">{caption}</figcaption>}
    </figure>
  );
}

function GalleryCropEditor({
  asset,
  cropX,
  onChange,
}: {
  asset: MediaAsset;
  cropX: number;
  onChange: (cropX: number) => void;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const measureStage = () => {
    const stage = stageRef.current;
    if (!stage) return;
    const { width, height } = stage.getBoundingClientRect();
    setStageSize({ width, height });
  };
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const observer = new ResizeObserver(measureStage);
    observer.observe(stage);
    measureStage();
    return () => observer.disconnect();
  }, [asset.id]);
  const updateFromPointer = (clientX: number) => {
    const stage = stageRef.current;
    if (!stage) return;
    const { left, width, height } = stage.getBoundingClientRect();
    const travel = width - height;
    if (travel <= 0) return;
    onChange(Math.round(Math.max(0, Math.min(100, ((clientX - left - height / 2) / travel) * 100))));
  };
  const cropLeft = Math.max(0, ((stageSize.width - stageSize.height) * cropX) / 100);
  return (
    <figure className="space-y-1">
      <div
        ref={stageRef}
        className="relative touch-none overflow-hidden rounded-md border"
        onPointerMove={event => {
          if (event.currentTarget.hasPointerCapture(event.pointerId)) updateFromPointer(event.clientX);
        }}
      >
        <img src={`/api/media/${asset.id}/file?width=1000`} alt="" className="block w-full" onLoad={measureStage} />
        {stageSize.height > 0 && (
          <>
            <div
              className="pointer-events-none absolute top-0 border-2 border-white shadow-[0_0_0_9999px_rgba(255,255,255,0.68)]"
              style={{ width: stageSize.height, height: stageSize.height, left: cropLeft }}
            />
            <button
              type="button"
              aria-label="Verschuif vierkante uitsnede"
              className="absolute top-0 cursor-ew-resize bg-transparent"
              style={{ width: stageSize.height, height: stageSize.height, left: cropLeft }}
              onPointerDown={event => {
                stageRef.current?.setPointerCapture(event.pointerId);
                updateFromPointer(event.clientX);
              }}
            />
          </>
        )}
      </div>
      <figcaption className="text-xs text-muted-foreground">
        Driekolom: sleep het vierkante kader naar links of rechts. Het gedimde deel verschijnt niet in de vierkante
        kaart.
      </figcaption>
    </figure>
  );
}

export function MediaLibrary({
  value,
  onChange,
  variant = 'case',
}: {
  value: CaseMediaSelection[];
  onChange: (value: CaseMediaSelection[]) => void;
  variant?: 'case' | 'blog';
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { data: assets } = useQuery({ queryKey: ['media'], queryFn: () => api.get<MediaAsset[]>('/media') });
  const [uploadingRole, setUploadingRole] = useState<SelectableRole>();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadName, setUploadName] = useState<string>();
  const hero = value.find(entry => entry.role === 'hero');
  const gallery = value.find(entry => entry.role === 'gallery');
  const assetFor = (entry?: CaseMediaSelection) => assets?.find(asset => asset.id === entry?.mediaAssetId);

  const setRole = (role: SelectableRole, mediaAssetId: string) =>
    onChange([...value.filter(entry => entry.role !== role), { mediaAssetId, role, sortOrder: 0, cropX: 50 }]);
  const removeRole = (role: SelectableRole) => onChange(value.filter(entry => entry.role !== role));
  const updateGalleryCrop = (cropX: number) =>
    onChange(value.map(entry => (entry.role === 'gallery' ? { ...entry, cropX } : entry)));
  const chooseUpload = (role: SelectableRole) => {
    setUploadingRole(role);
    inputRef.current?.click();
  };
  const upload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const role = uploadingRole;
    setUploadingRole(undefined);
    if (!file || !role) return;
    setUploadName(file.name);
    setIsUploading(true);
    try {
      const body = new FormData();
      body.append('file', file);
      body.append('intendedRole', role);
      const asset = await api.postForm<MediaAsset>('/media', body);
      // Make the selected image available in this render immediately. Waiting
      // only for invalidation can briefly leave the role pointing at an asset
      // absent from the stale query result.
      queryClient.setQueryData<MediaAsset[]>(['media'], current => {
        const withoutAsset = (current ?? []).filter(currentAsset => currentAsset.id !== asset.id);
        return [asset, ...withoutAsset];
      });
      void queryClient.invalidateQueries({ queryKey: ['media'] });
      setRole(role, asset.id);
      toast.success(role === 'hero' ? 'Hero-afbeelding ingesteld.' : 'Galerijafbeelding ingesteld.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Uploaden is niet gelukt.');
    } finally {
      setIsUploading(false);
      setUploadName(undefined);
    }
  };
  const renderRole = (role: SelectableRole, label: string, description: string) => {
    const entry = role === 'hero' ? hero : gallery;
    const asset = assetFor(entry);
    return (
      <div className="space-y-3 rounded-md border p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 font-medium">
              {role === 'hero' ? <Star className="size-4" /> : <ImagePlus className="size-4" />}
              {label}
            </p>
            <p className="max-w-lg text-sm text-muted-foreground">{description}</p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={() => chooseUpload(role)}
            disabled={Boolean(uploadingRole) || isUploading}
          >
            <Upload />
            {isUploading ? 'Uploaden en optimaliseren…' : asset ? 'Vervang afbeelding' : 'Afbeelding uploaden'}
          </Button>
        </div>
        {asset ? (
          <div className="max-w-2xl space-y-4">
            {role === 'gallery' && entry && variant === 'case' ? (
              <>
                <GalleryCropEditor asset={asset} cropX={entry.cropX} onChange={updateGalleryCrop} />
                <Preview asset={asset} caption="Tweekolom: volledig landschap" />
              </>
            ) : (
              <Preview asset={asset} />
            )}
            <div className="space-y-3">
              <p className="text-sm font-medium">{asset.originalFilename}</p>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => removeRole(role)}
              >
                <Trash2 />
                Verwijder
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-md border border-dashed bg-muted/30 p-5 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">
              Nog geen {role === 'hero' ? 'hero' : 'galerij'}afbeelding gekozen.
            </p>
            <p className="mt-1">PNG, JPEG, GIF of WebP, maximaal 20 MB. Liggend of vierkant werkt het best.</p>
          </div>
        )}
        {isUploading && (
          <p
            aria-live="polite"
            className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm text-foreground"
          >
            <span className="font-medium">{uploadName ?? 'Afbeelding'} wordt geüpload.</span> We maken meteen de
            webformaten (480, 1000 en 2000 px); de afbeelding verschijnt pas wanneer alles klaarstaat.
          </p>
        )}
      </div>
    );
  };

  return (
    <section className="space-y-4">
      <div>
        <p className="font-medium">Kaartafbeeldingen</p>
        <p className="text-sm text-muted-foreground">
          Upload één hero en één galerijafbeelding.{' '}
          {variant === 'blog'
            ? 'De galerijafbeelding blijft volledig zichtbaar als landschap op de blogkaart.'
            : 'Voor beide accepteren we alleen liggende of vierkante bestanden; portretbeelden passen niet betrouwbaar in de kaarten.'}
        </p>
      </div>
      <input
        ref={inputRef}
        className="hidden"
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp"
        onChange={upload}
      />
      {renderRole(
        'hero',
        'Hero-afbeelding',
        variant === 'blog' ? 'Voor de bovenkant van het artikel.' : 'Voor de bovenkant van de individuele casepagina.'
      )}
      {renderRole(
        'gallery',
        'Galerijafbeelding',
        variant === 'blog'
          ? 'Voor de blogkaart; geen vierkante uitsnede.'
          : 'Voor de kaarten in de twee- en driekolomsoverzichten.'
      )}
    </section>
  );
}
